import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserCheck, UserX, Shield, Heart, Search, UserPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { userConnectionsService } from '@/services/userConnectionsService';

interface TeamMember {
  user_id: string;
  member_type: 'player' | 'coach' | 'support';
  position?: string;
  role?: string;
  game_position?: string; // Position assigned for this specific game
  user_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_photo_url?: string;
  };
}

interface GameRosterModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  teamId: number;
  teamName: string;
  teamSide: 'home' | 'away';
  currentUserId: string;
  userRole: string;
  currentUserTeams?: number[]; // Teams the current user manages/coaches
  sportName?: string; // Sport for the game to show appropriate positions
}

export const GameRosterModal = ({ 
  isOpen, 
  onClose, 
  gameId, 
  teamId, 
  teamName, 
  teamSide,
  currentUserId,
  userRole,
  currentUserTeams = [],
  sportName
}: GameRosterModalProps) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [playerPositions, setPlayerPositions] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [connectionPlayers, setConnectionPlayers] = useState<TeamMember[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkManagementPermissions();
      loadTeamMembers();
      loadExistingRoster();
      loadConnectionPlayers();
    }
  }, [isOpen, teamId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        searchConnections(searchTerm);
      } else {
        loadConnectionPlayers();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const checkManagementPermissions = async () => {
    try {
      // Venue owners and admins can manage any roster
      if (['venue', 'admin'].includes(userRole)) {
        setCanManage(true);
        return;
      }

      // Check if user is team owner or coach for this specific team
      const isTeamManager = currentUserTeams.includes(teamId);
      
      if (isTeamManager) {
        setCanManage(true);
        return;
      }

      // Additional verification from database
      const { data: team } = await supabase
        .from('teams')
        .select('created_by')
        .eq('team_id', teamId)
        .maybeSingle();

      if (team?.created_by === currentUserId) {
        setCanManage(true);
        return;
      }

      const { data: coach } = await supabase
        .from('team_coaches')
        .select('coach_id')
        .eq('team_id', teamId)
        .eq('coach_id', currentUserId)
        .maybeSingle();

      setCanManage(!!coach);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManage(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const allMembers: TeamMember[] = [];

      // Load team players
      const { data: players, error: playersError } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', teamId);

      if (playersError) throw playersError;

      for (const player of players || []) {
        const userDetails = await getUserDetails(player.user_id);
        allMembers.push({
          user_id: player.user_id,
          member_type: 'player',
          position: player.position,
          user_details: userDetails
        });
      }

      // Load team coaches
      const { data: coaches, error: coachesError } = await supabase
        .from('team_coaches')
        .select('*')
        .eq('team_id', teamId);

      if (coachesError) throw coachesError;

      for (const coach of coaches || []) {
        const userDetails = await getUserDetails(coach.coach_id);
        allMembers.push({
          user_id: coach.coach_id,
          member_type: 'coach',
          role: coach.role,
          user_details: userDetails
        });
      }

      setTeamMembers(allMembers);
    } catch (error) {
      console.error('Error loading team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingRoster = async () => {
    try {
      // Load from game_rosters table with positions
      const { data: rosterData, error } = await supabase
        .from('game_rosters')
        .select('player_id, position')
        .eq('game_id', gameId)
        .eq('team_side', teamSide);

      if (error) throw error;

      const rosterIds = rosterData?.map(r => r.player_id) || [];
      const positions: Record<string, string> = {};
      
      // Store positions for each player
      rosterData?.forEach(row => {
        if (row.position) {
          positions[row.player_id] = row.position;
        }
      });
      
      setSelectedMembers(rosterIds);
      setPlayerPositions(positions);
      
      console.log('🎯 Loaded existing roster:', {
        rosterIds,
        positions,
        teamId,
        gameId
      });
    } catch (error) {
      console.error('Error loading existing roster:', error);
    }
  };

  const getUserDetails = async (userId: string) => {
    try {
      // Try Supabase profiles first
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, first_name, last_name, profile_photo_url')
        .eq('id', userId)
        .maybeSingle();

      if (profile) return profile;

      // Try internal users
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('username')
        .eq('id', userId)
        .maybeSingle();

      if (internalUser) {
        return {
          username: internalUser.username,
          first_name: '',
          last_name: '',
          profile_photo_url: null
        };
      }

      // Return fallback with user ID for display
      return {
        username: `User ${userId.slice(0, 8)}`,
        first_name: '',
        last_name: '',
        profile_photo_url: null
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {
        username: `User ${userId.slice(0, 8)}`,
        first_name: '',
        last_name: '',
        profile_photo_url: null
      };
    }
  };

  const loadConnectionPlayers = async () => {
    try {
      setLoadingConnections(true);
      const connections = await userConnectionsService.getConnections(currentUserId, 'connect');
      const connectedPlayers: TeamMember[] = [];

      for (const connection of connections) {
        if (connection.status === 'accepted') {
          const userDetails = await getUserDetails(connection.connected_user_id);
          connectedPlayers.push({
            user_id: connection.connected_user_id,
            member_type: 'player',
            user_details: userDetails
          });
        }
      }

      setConnectionPlayers(connectedPlayers);
    } catch (error) {
      console.error('Error loading connection players:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  const searchConnections = async (query: string) => {
    try {
      setLoadingConnections(true);
      
      // Search in profiles and internal users
      const [profilesResult, internalResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, username, first_name, last_name, profile_photo_url')
          .or(`username.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
          .limit(10),
        supabase
          .from('internal_users')
          .select('id, username')
          .ilike('username', `%${query}%`)
          .limit(10)
      ]);

      const searchResults: TeamMember[] = [];

      // Add profiles results
      if (profilesResult.data) {
        for (const profile of profilesResult.data) {
          // Don't include already team members or the current user
          const isTeamMember = teamMembers.some(m => m.user_id === profile.id);
          if (!isTeamMember && profile.id !== currentUserId) {
            searchResults.push({
              user_id: profile.id,
              member_type: 'player',
              user_details: {
                username: profile.username,
                first_name: profile.first_name,
                last_name: profile.last_name,
                profile_photo_url: profile.profile_photo_url
              }
            });
          }
        }
      }

      // Add internal users results
      if (internalResult.data) {
        for (const user of internalResult.data) {
          const isTeamMember = teamMembers.some(m => m.user_id === user.id);
          if (!isTeamMember && user.id !== currentUserId) {
            searchResults.push({
              user_id: user.id,
              member_type: 'player',
              user_details: {
                username: user.username,
                first_name: '',
                last_name: '',
                profile_photo_url: null
              }
            });
          }
        }
      }

      setConnectionPlayers(searchResults);
    } catch (error) {
      console.error('Error searching connections:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleMemberToggle = (memberId: string) => {
    if (!canManage) {
      toast.error('You do not have permission to manage this roster');
      return;
    }
    
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const getPositionOptions = () => {
    const sport = sportName?.toLowerCase();
    
    if (sport === 'soccer' || sport === 'football') {
      return ['Forward', 'Midfielder', 'Defender', 'Goalkeeper', 'Substitute'];
    } else if (sport === 'basketball') {
      return ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center', 'Substitute'];
    } else if (sport === 'baseball') {
      return ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop', 'Left Field', 'Center Field', 'Right Field', 'Substitute'];
    } else {
      // Default positions for unknown sports
      return ['Forward', 'Midfielder', 'Defender', 'Goalkeeper', 'Substitute'];
    }
  };

  const handlePositionChange = (playerId: string, position: string) => {
    setPlayerPositions(prev => ({
      ...prev,
      [playerId]: position
    }));
  };

  const handleSubmitRoster = async () => {
    if (!canManage) {
      toast.error('You do not have permission to manage this roster');
      return;
    }

    try {
      setSubmitting(true);

      // Remove existing roster entries for this team/game
        await supabase
        .from('game_rosters')
        .delete()
        .eq('game_id', gameId)
        .eq('team_side', teamSide);

        // Add selected members to game roster
        if (selectedMembers.length > 0) {
          const rosterEntries = selectedMembers.map(memberId => {
            return {
              game_id: gameId,
              team_id: teamId,
              player_id: memberId,
              team_side: teamSide,
              player_type: 'supabase',
              added_by: currentUserId,
              is_eligible: true,
              eligibility_status: 'eligible',
              position: playerPositions[memberId] || null
            };
          });

        const { error } = await supabase
          .from('game_rosters')
          .insert(rosterEntries);

        if (error) {
          console.error('Roster insert error:', error);
          throw error;
        }
      }

      toast.success('Game roster updated successfully');
      onClose();
    } catch (error) {
      console.error('Error submitting roster:', error);
      toast.error('Failed to update game roster: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const getMemberDisplayName = (member: TeamMember) => {
    const details = member.user_details;
    return details?.username || 
           `${details?.first_name || ''} ${details?.last_name || ''}`.trim() ||
           'Unknown Member';
  };

  const getMemberIcon = (type: string) => {
    switch (type) {
      case 'coach':
        return <Shield className="h-4 w-4" />;
      case 'support':
        return <Heart className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getMemberTypeColor = (type: string) => {
    switch (type) {
      case 'coach':
        return 'border-blue-500 text-blue-400';
      case 'support':
        return 'border-purple-500 text-purple-400';
      default:
        return 'border-sports-green text-sports-green';
    }
  };

  const players = teamMembers.filter(m => m.member_type === 'player');
  const coaches = teamMembers.filter(m => m.member_type === 'coach');
  const supportStaff = teamMembers.filter(m => m.member_type === 'support');

  if (!canManage && !loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-red-400">Access Denied</DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <p className="text-gray-300">
              You do not have permission to manage the roster for this team. 
              Only team owners and coaches can modify rosters.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={onClose} variant="outline" className="border-gray-600 text-gray-300">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-gray-900 border-gray-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sports-green">
            {['venue', 'admin'].includes(userRole) ? 'View' : 'Manage'} Game Roster - {teamName} ({teamSide} team)
          </DialogTitle>
          <p className="text-sm text-gray-400 mt-2">
            {['venue', 'admin'].includes(userRole) 
              ? 'Review team roster for eligibility verification. Only coaches and team owners can modify rosters.'
              : 'Select team members who will participate in this game. This roster determines who is eligible to play and will be validated by venue staff before the game starts.'
            }
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
              <p className="mt-2 text-gray-400">Loading team members...</p>
            </div>
          ) : (
            <>
              {/* Players Section */}
              {players.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="h-5 w-5 text-sports-green" />
                      Players ({players.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {players.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                        >
                          <Checkbox
                            id={member.user_id}
                            checked={selectedMembers.includes(member.user_id)}
                            onCheckedChange={() => handleMemberToggle(member.user_id)}
                            className="border-sports-green data-[state=checked]:bg-sports-green"
                          />
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={member.user_details?.profile_photo_url || ''} />
                            <AvatarFallback className="bg-sports-green text-black text-sm">
                              {(member.user_details?.username || member.user_details?.first_name || 'U').charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white truncate">
                              {getMemberDisplayName(member)}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {member.position && (
                                <Badge variant="outline" className={getMemberTypeColor('player')}>
                                  Team: {member.position}
                                </Badge>
                              )}
                              {selectedMembers.includes(member.user_id) && (
                                <Select
                                  value={playerPositions[member.user_id] || ''}
                                  onValueChange={(value) => handlePositionChange(member.user_id, value)}
                                >
                                  <SelectTrigger className="w-32 h-6 text-xs bg-gray-600 border-gray-500">
                                    <SelectValue placeholder="Game position" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No Position</SelectItem>
                                    {getPositionOptions().filter(position => position && position.trim()).map(position => (
                                      <SelectItem key={position} value={position}>{position}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                          {selectedMembers.includes(member.user_id) ? (
                            <UserCheck className="h-5 w-5 text-sports-green" />
                          ) : (
                            <UserX className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Coaches Section */}
              {coaches.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-400" />
                      Coaches ({coaches.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {coaches.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                        >
                          <Checkbox
                            id={member.user_id}
                            checked={selectedMembers.includes(member.user_id)}
                            onCheckedChange={() => handleMemberToggle(member.user_id)}
                            className="border-blue-400 data-[state=checked]:bg-blue-400"
                          />
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={member.user_details?.profile_photo_url || ''} />
                            <AvatarFallback className="bg-blue-500 text-white text-sm">
                              {(member.user_details?.username || member.user_details?.first_name || 'C').charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="font-medium text-white">
                              {getMemberDisplayName(member)}
                            </div>
                            {member.role && (
                              <Badge variant="outline" className={getMemberTypeColor('coach')}>
                                {member.role.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                          {selectedMembers.includes(member.user_id) ? (
                            <UserCheck className="h-5 w-5 text-blue-400" />
                          ) : (
                            <UserX className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Support Staff Section */}
              {supportStaff.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Heart className="h-5 w-5 text-purple-400" />
                      Support Staff ({supportStaff.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {supportStaff.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                        >
                          <Checkbox
                            id={member.user_id}
                            checked={selectedMembers.includes(member.user_id)}
                            onCheckedChange={() => handleMemberToggle(member.user_id)}
                            className="border-purple-400 data-[state=checked]:bg-purple-400"
                          />
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={member.user_details?.profile_photo_url || ''} />
                            <AvatarFallback className="bg-purple-500 text-white text-sm">
                              {(member.user_details?.username || member.user_details?.first_name || 'S').charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="font-medium text-white">
                              {getMemberDisplayName(member)}
                            </div>
                            {member.role && (
                              <Badge variant="outline" className={getMemberTypeColor('support')}>
                                {member.role}
                              </Badge>
                            )}
                          </div>
                          {selectedMembers.includes(member.user_id) ? (
                            <UserCheck className="h-5 w-5 text-purple-400" />
                          ) : (
                            <UserX className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {teamMembers.length === 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No team members found</p>
                  </CardContent>
                </Card>
              )}

              {/* Add Players from Connections/Search Section */}
              {canManage && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-orange-400" />
                      Add Additional Players
                    </CardTitle>
                    <p className="text-sm text-gray-400 mt-1">
                      Search for players from your connections or find new players to add to this game roster.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search players by username or name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                      
                      {loadingConnections ? (
                        <div className="text-center py-4">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orange-400"></div>
                          <p className="mt-2 text-gray-400 text-sm">Searching...</p>
                        </div>
                      ) : connectionPlayers.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                          {connectionPlayers.map((player) => (
                            <div
                              key={player.user_id}
                              className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                            >
                              <Checkbox
                                id={`connection-${player.user_id}`}
                                checked={selectedMembers.includes(player.user_id)}
                                onCheckedChange={() => handleMemberToggle(player.user_id)}
                                className="border-orange-400 data-[state=checked]:bg-orange-400"
                              />
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={player.user_details?.profile_photo_url || ''} />
                                <AvatarFallback className="bg-orange-500 text-white text-sm">
                                  {(player.user_details?.username || player.user_details?.first_name || 'P').charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-white truncate">
                                  {getMemberDisplayName(player)}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  <Badge variant="outline" className="border-orange-500 text-orange-400">
                                    Additional Player
                                  </Badge>
                                  {selectedMembers.includes(player.user_id) && (
                                    <Select
                                      value={playerPositions[player.user_id] || ''}
                                      onValueChange={(value) => handlePositionChange(player.user_id, value)}
                                    >
                                      <SelectTrigger className="w-32 h-6 text-xs bg-gray-600 border-gray-500">
                                        <SelectValue placeholder="Game position" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">No Position</SelectItem>
                                        {getPositionOptions().filter(position => position && position.trim()).map(position => (
                                          <SelectItem key={position} value={position}>{position}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              </div>
                              {selectedMembers.includes(player.user_id) ? (
                                <UserCheck className="h-5 w-5 text-orange-400" />
                              ) : (
                                <UserX className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          ))}
                        </div>
                      ) : searchTerm.trim() ? (
                        <div className="text-center py-4">
                          <Search className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                          <p className="text-gray-400 text-sm">No players found matching "{searchTerm}"</p>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <UserPlus className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                          <p className="text-gray-400 text-sm">Search for players to add to your roster</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between bg-gray-800 p-4 rounded-lg">
                <div className="text-sm text-gray-400">
                  <div className="font-semibold text-white mb-1">Selection Summary:</div>
                  <div>Team Players: {selectedMembers.filter(id => {
                    const player = players.find(p => p.user_id === id);
                    return player && player.user_details && (player.user_details.username || player.user_details.first_name);
                  }).length}</div>
                  <div>Coaches: {selectedMembers.filter(id => {
                    const coach = coaches.find(c => c.user_id === id);
                    return coach && coach.user_details && (coach.user_details.username || coach.user_details.first_name);
                  }).length}</div>
                  <div>Additional Players: {selectedMembers.filter(id => {
                    const connectionPlayer = connectionPlayers.find(cp => cp.user_id === id);
                    const isNotTeamMember = !teamMembers.find(tm => tm.user_id === id);
                    return connectionPlayer && connectionPlayer.user_details && (connectionPlayer.user_details.username || connectionPlayer.user_details.first_name) && isNotTeamMember;
                  }).length}</div>
                  <div>Total Selected: {selectedMembers.filter(id => {
                    // Count only players with valid display data
                    const teamMember = teamMembers.find(tm => tm.user_id === id);
                    const connectionPlayer = connectionPlayers.find(cp => cp.user_id === id);
                    const hasValidData = (teamMember?.user_details && (teamMember.user_details.username || teamMember.user_details.first_name)) ||
                                       (connectionPlayer?.user_details && (connectionPlayer.user_details.username || connectionPlayer.user_details.first_name));
                    return hasValidData;
                  }).length}</div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitRoster}
                    disabled={submitting || !canManage}
                    className="bg-sports-green text-black hover:bg-green-600"
                  >
                    {submitting ? 'Updating...' : 'Update Roster'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};